import React from 'react';
import { Link } from 'react-router-dom';

interface Product {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  color: string;
  price: string;
}

const products: Product[] = [
  {
    id: 'cimmpleflow',
    name: 'CimmpleFlow',
    description: 'Complete Information on Manufacturing Parts, Planning and Logistics Evaluation. Cloud-based ERP solution available anytime, anywhere.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    features: ['Quotations', 'Order & Job Management', 'Purchasing & Quality Control', 'Reporting & Accounting'],
    color: 'primary',
    price: 'From $49/user/mo',
  },
  {
    id: 'cimmlepay',
    name: 'CimmplePay',
    description: 'An intelligent payroll management solution that automates salary processing, tax calculations, and compliance reporting.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    features: ['Automated calculations', 'Tax compliance', 'Employee self-service', 'Direct deposits'],
    color: 'accent',
    price: '$25/month',
  },
];

const ProductsSection: React.FC = () => {
  return (
    <section id="products" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-4">
            Our Products
          </span>
          <h2 className="section-title">
            Software products built for
            <br />
            <span className="gradient-text">modern businesses</span>
          </h2>
          <p className="section-subtitle mx-auto mt-4">
            Powerful, cloud-based solutions designed to optimize your business operations and drive growth.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {products.map((product, index) => (
            <Link
              key={product.id}
              to={`/products/${product.id}`}
              className="group card-glow relative overflow-hidden"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Gradient overlay on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${
                product.color === 'primary' 
                  ? 'from-primary-500/0 to-primary-500/0 group-hover:from-primary-500/5 group-hover:to-primary-500/10' 
                  : 'from-accent-500/0 to-accent-500/0 group-hover:from-accent-500/5 group-hover:to-accent-500/10'
              } transition-all duration-500`} />
              
              <div className="relative">
                {/* Header with Icon and Price */}
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-16 h-16 rounded-2xl ${
                    product.color === 'primary' 
                      ? 'bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400' 
                      : 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-400'
                  } flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    {product.icon}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {product.id === 'cimmlepay' && (
                      <span className="px-3 py-1 rounded-full bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-400 text-xs font-medium">
                        Coming Soon
                      </span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      product.color === 'primary' 
                        ? 'bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400' 
                        : 'bg-accent-100 dark:bg-accent-500/10 text-accent-700 dark:text-accent-400'
                    }`}>
                      {product.price}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <h3 className={`text-2xl font-display font-bold mb-3 transition-colors duration-300 ${
                  product.color === 'primary'
                    ? 'text-slate-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400'
                    : 'text-slate-900 dark:text-white group-hover:text-accent-600 dark:group-hover:text-accent-400'
                }`}>
                  {product.name}
                </h3>
                <p className="text-slate-600 dark:text-dark-300 mb-6">
                  {product.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {product.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700 dark:text-dark-400">
                      <svg className={`w-4 h-4 ${product.color === 'primary' ? 'text-primary-600 dark:text-primary-500' : 'text-accent-600 dark:text-accent-500'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Learn More */}
                <div className={`flex items-center gap-2 font-semibold ${
                  product.color === 'primary' ? 'text-primary-600 dark:text-primary-400' : 'text-accent-600 dark:text-accent-400'
                } group-hover:gap-4 transition-all duration-300`}>
                  Learn more
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* View All Products */}
        <div className="text-center mt-12">
          <Link to="/products" className="btn-secondary inline-flex items-center gap-2">
            View All Products
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
